import { useState, useEffect } from 'react';
import { getAuthHeaders, handleAuthError } from '../utils/authUtils';
import { getApiBaseUrlWithoutPath } from '../../config/environment';
import { displayOrderStatusToApiSlug } from '../utils/adminOrderStatusApi';
import { extractSheenBackendErrorMessageLoose } from '../../utils/apiErrorMessage';

/** Mongo / API order id → string for URL (handles $oid, nested _id) */
const toOrderIdString = (orderId) => {
  if (orderId == null || orderId === '') return '';
  if (typeof orderId === 'object') {
    if (orderId.$oid) return String(orderId.$oid);
    if (orderId._id != null) return toOrderIdString(orderId._id);
  }
  return String(orderId);
};

const useOrders = (filters = {}) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const fetchOrders = async (newFilters = {}) => {
    try {
      setLoading(true);
      setError(null);

      const headers = getAuthHeaders();
      const apiUrl = getApiBaseUrlWithoutPath();

      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
        ...newFilters
      });

      const response = await fetch(`${apiUrl}/api/admin/orders?${queryParams}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        handleAuthError(new Error(`HTTP ${response.status}`), response);
      }

      const result = await response.json();

      // Many backends omit `success` on GET; only treat explicit failure as error
      if (result.success === false) {
        throw new Error(result.message || result.error || 'Failed to fetch orders');
      }

      const ordersData = result.data ?? result.orders ?? [];
      if (!Array.isArray(ordersData)) {
        throw new Error(result.message || 'Invalid orders list from server');
      }

      const paginationData = result.pagination ?? {
        page: 1,
        limit: pagination.limit,
        total: ordersData.length,
        totalPages: 1
      };
      setOrders(ordersData);
      setPagination(paginationData);
    } catch (error) {
      console.error('Orders error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [pagination.page]);

  const updateOrderStatus = async (orderId, status) => {
    try {
      setLoading(true);
      setError(null);

      const headers = getAuthHeaders();
      const apiUrl = getApiBaseUrlWithoutPath();

      const id = toOrderIdString(orderId);
      if (!id) {
        throw new Error('Invalid order id');
      }

      const response = await fetch(
        `${apiUrl}/api/admin/orders/${encodeURIComponent(id)}/status`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ status: displayOrderStatusToApiSlug(status) }),
        }
      );

      const rawText = await response.text();
      let result = {};
      try {
        result = rawText ? JSON.parse(rawText) : {};
      } catch {
        result = { error: rawText ? rawText.slice(0, 400) : '' };
      }

      if (!response.ok) {
        const msg =
          extractSheenBackendErrorMessageLoose(result, response.status) ||
          `HTTP ${response.status}`;
        handleAuthError(new Error(msg), response);
      }

      if (result.success === false) {
        throw new Error(result.message || result.error || 'Failed to update order status');
      }
      await fetchOrders();
      return result;
    } catch (error) {
      console.error('Update order status error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateOrderTracking = async (orderId, trackingData) => {
    try {
      setLoading(true);
      setError(null);

      const headers = getAuthHeaders();
      const apiUrl = getApiBaseUrlWithoutPath();

      const response = await fetch(`${apiUrl}/api/admin/orders/${orderId}/tracking`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(trackingData)
      });

      if (!response.ok) {
        handleAuthError(new Error(`HTTP ${response.status}`), response);
      }

      const result = await response.json();

      if (result.success === false) {
        throw new Error(result.message || 'Failed to update order tracking');
      }
      await fetchOrders();
      return result;
    } catch (error) {
      console.error('Update order tracking error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const cancelOrder = async (orderId, reason) => {
    try {
      setLoading(true);
      setError(null);

      const id = toOrderIdString(orderId);
      if (!id) {
        throw new Error('Invalid order id');
      }

      // Backend often validates `reason` — JSON.stringify omits undefined, so send a default
      const resolvedReason =
        typeof reason === 'string' && reason.trim() ? reason.trim() : 'Cancelled by admin';

      const headers = getAuthHeaders();
      const apiUrl = getApiBaseUrlWithoutPath();

      const response = await fetch(
        `${apiUrl}/api/admin/orders/${encodeURIComponent(id)}/cancel`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify({ reason: resolvedReason })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const msg =
          errorData.message ||
          errorData.error ||
          (Array.isArray(errorData.errors)
            ? errorData.errors
                .map((e) => (typeof e === 'string' ? e : e?.msg || e?.message))
                .filter(Boolean)
                .join(', ')
            : null) ||
          `HTTP ${response.status}`;
        if (response.status === 401) {
          handleAuthError(new Error(msg), response);
        }
        throw new Error(msg);
      }

      const result = await response.json();
      
      if (result.success) {
        await fetchOrders(); // Refresh the orders list
        return result;
      } else {
        throw new Error(result.message || 'Failed to cancel order');
      }
    } catch (error) {
      console.error('Cancel order error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refundOrder = async (orderId, refundData) => {
    try {
      setLoading(true);
      setError(null);

      const headers = getAuthHeaders();
      const apiUrl = getApiBaseUrlWithoutPath();

      const response = await fetch(`${apiUrl}/api/admin/orders/${orderId}/refund`, {
        method: 'POST',
        headers,
        body: JSON.stringify(refundData)
      });

      if (!response.ok) {
        handleAuthError(new Error(`HTTP ${response.status}`), response);
      }

      const result = await response.json();
      
      if (result.success) {
        await fetchOrders(); // Refresh the orders list
        return result;
      } else {
        throw new Error(result.message || 'Failed to process refund');
      }
    } catch (error) {
      console.error('Refund order error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getOrderById = async (orderId) => {
    try {
      const headers = getAuthHeaders();
      const apiUrl = getApiBaseUrlWithoutPath();

      // Use regular orders endpoint - admin backend typically allows admin token to view any order
      const response = await fetch(`${apiUrl}/api/orders/${orderId}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 404) {
          throw new Error('Order not found');
        }
        if (response.status === 403) {
          throw new Error(errorData.message || errorData.error || 'You do not have permission to view this order');
        }
        if (response.status === 500) {
          throw new Error(errorData.message || errorData.error || 'Server error. Please try again.');
        }
        handleAuthError(new Error(`HTTP ${response.status}`), response);
      }

      const result = await response.json();
      const orderData = result.data ?? result.order ?? result;
      
      if (result.success || orderData) {
        return orderData;
      } else {
        throw new Error(result.message || result.error || 'Failed to fetch order details');
      }
    } catch (error) {
      console.error('Get order by ID error:', error);
      throw error;
    }
  };

  return { 
    orders, 
    loading, 
    error, 
    pagination, 
    refetch: fetchOrders,
    updateOrderStatus,
    updateOrderTracking,
    cancelOrder,
    refundOrder,
    getOrderById
  };
};

export default useOrders;
